// Public teaser landing. Everything is locked behind the paywall.
// ANY 3 clicks on a teaser element (bass-clef, flag, or page-list item) opens the auth gate.
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock, LogIn, UserPlus, X, Map as MapIcon, BookOpen } from "lucide-react";
import BassClefShip from "../components/BassClefShip";
import CountryFlagGrid from "../components/CountryFlagGrid";
import { RIGHTS_TOPICS, TOPIC_SLUGS } from "../data/rightsTopics";
import { useAuth } from "../context/AuthContext";

// Public list of every page on the site. Order = your 57-topic list, layered into the app skeleton.
const PAGES_IN_ORDER = [
  // Public surface (visitors land here)
  { n: "01", label: "Landing — Hero & member gate", path: "/" },
  { n: "02", label: "Create an account", path: "/register" },
  { n: "03", label: "Member sign in", path: "/login" },
  { n: "04", label: "Site Map", path: "/sitemap" },

  // Member workspace
  { n: "05", label: "Dashboard", path: "/dashboard", locked: true },
  { n: "06", label: "Profile", path: "/profile", locked: true },
  { n: "07", label: "Network — discover members", path: "/network", locked: true },
  { n: "08", label: "Connections", path: "/connections", locked: true },
  { n: "09", label: "Upgrade · $6.95 Complete Access", path: "/upgrade", locked: true },
  { n: "10", label: "Payment Success", path: "/payment/success", locked: true },

  // Directory
  { n: "11", label: "Agency directory — 200+ agencies", path: "/agencies", locked: true },
  { n: "12", label: "Master Rights Hub", path: "/rights/master", locked: true },
  { n: "13", label: "Digital Rights Hub", path: "/rights/digital", locked: true },
  { n: "14", label: "Recording Rights Hub", path: "/rights/recording", locked: true },
  { n: "15", label: "Rights Library — all 57 topics", path: "/rights-library", locked: true },
  { n: "16", label: "Copyright Laws", path: "/copyright-laws", locked: true },
  { n: "17", label: "Translation Hub", path: "/translate", locked: true },
  { n: "18", label: "Orchestration Directory", path: "/orchestration", locked: true },

  // 57 rights topics — in your specified order
  ...TOPIC_SLUGS.map((slug, i) => ({
    n: String(19 + i).padStart(2, "0"),
    label: RIGHTS_TOPICS[slug].title,
    path: `/rights-topic/${slug}`,
    locked: true,
  })),

  // Legal (public)
  { n: String(19 + TOPIC_SLUGS.length).padStart(2, "0"), label: "Terms & Conditions", path: "/legal/terms" },
  { n: String(20 + TOPIC_SLUGS.length).padStart(2, "0"), label: "Privacy Policy", path: "/legal/privacy" },
  { n: String(21 + TOPIC_SLUGS.length).padStart(2, "0"), label: "Disclaimer", path: "/legal/disclaimer" },
  { n: String(22 + TOPIC_SLUGS.length).padStart(2, "0"), label: "Refund Policy", path: "/legal/refund" },
];

export default function Landing() {
  const { user } = useAuth();
  const isSignedIn = !!user;
  const [clicks, setClicks] = useState(0);
  const [showGate, setShowGate] = useState(false);
  const nav = useNavigate();

  const handleTease = useCallback(() => {
    if (isSignedIn) return;
    setClicks((c) => c + 1);
  }, [isSignedIn]);

  // After 3 teasing clicks (anywhere — bass clef, flag, or page-list entry), open the gate
  useEffect(() => {
    if (clicks >= 3) {
      setShowGate(true);
      setClicks(0);
    }
  }, [clicks]);

  // Reset click streak if user pauses (>1.8s between clicks)
  useEffect(() => {
    if (clicks === 0) return;
    const t = setTimeout(() => setClicks(0), 1800);
    return () => clearTimeout(t);
  }, [clicks]);

  // ============ Signed-in view: Bass clef top, Flags, Rights below, zero clutter ============
  if (isSignedIn) {
    return (
      <div className="min-h-screen bg-[#0A1128] text-white pt-28 pb-24" data-testid="signed-in-view">
        {/* PILLAR 0 — Floating bass clef at the top */}
        <section className="section-pad" data-testid="signed-in-bassclef">
          <div className="max-w-7xl mx-auto px-6">
            <div className="relative w-full max-w-[520px] mx-auto aspect-[5/6] rounded-lg border border-white/5 bg-gradient-to-b from-[#0F1E40]/40 to-[#0A1128]/80 overflow-hidden shadow-[0_25px_80px_-30px_rgba(125,163,217,0.35)]">
              <BassClefShip />
              <div className="absolute left-5 bottom-5 right-5 text-[10px] uppercase tracking-[0.3em] text-[#7DA3D9]/80 font-semibold flex items-center justify-between pointer-events-none">
                <span>Bass Clef · OSRR</span>
                <span className="text-slate-500">International Music Rolodex</span>
              </div>
            </div>
          </div>
        </section>

        {/* PILLAR 1 — Country Flags */}
        <div data-testid="signed-in-flags">
          <CountryFlagGrid />
        </div>

        {/* PILLAR 2 — Rights & Categories directly beneath the flags */}
        <section className="border-t border-white/5 section-pad" data-testid="signed-in-rights">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-start gap-4 mb-10">
              <div className="w-12 h-12 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <div className="overline" style={{ color: "#D4AF37" }}>Rights &amp; categories</div>
                <h2 className="font-display text-3xl lg:text-5xl tracking-tight mt-1 leading-[1.05]">
                  Every rights topic on the registry
                </h2>
                <p className="text-slate-400 mt-3 max-w-2xl text-sm leading-relaxed">
                  Tap any topic to open its encyclopedic page — official government
                  authorities and private organizations for that right.
                </p>
              </div>
            </div>

            <ol className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="signed-in-rights-list">
              {TOPIC_SLUGS.map((slug, i) => {
                const t = RIGHTS_TOPICS[slug];
                return (
                  <li key={slug}>
                    <Link
                      to={`/rights-topic/${slug}`}
                      className="group flex items-start gap-3 w-full text-left p-4 rounded-md border border-white/10 hover:border-[#7DA3D9]/40 hover:bg-white/[0.03] transition-colors"
                      data-testid={`signed-in-topic-${slug}`}
                    >
                      <span className="font-display text-[#D4AF37] text-sm w-8 flex-shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                          {t.overline}
                        </div>
                        <div className="text-sm font-medium text-slate-200 group-hover:text-white truncate">
                          {t.title}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1128] text-white relative">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" aria-hidden />
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center mix-blend-luminosity"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg)" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/80 via-[#0A1128]/90 to-[#0A1128]" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 pt-40 pb-24 lg:pt-48 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 max-w-4xl fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#7DA3D9]/25 bg-[#7DA3D9]/5 mb-8">
                <ShieldCheck className="w-3.5 h-3.5 text-[#7DA3D9]" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#B8D0EE] font-semibold">
                  International Music Rolodex
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.98] tracking-tight">
                The professional registry for{" "}
                <span className="text-[#7DA3D9]">overseas rights</span> &amp; royalties.
              </h1>
              <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                OSRR is a members-only directory of 200+ musical rights organizations across 160
                countries. Access is granted after registration and a one-time $6.95 Complete-Access
                purchase.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/register" className="btn-gold inline-flex items-center gap-2" data-testid="cta-join-btn">
                  Create your account <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login" className="btn-ghost-gold inline-flex items-center gap-2" data-testid="cta-login-btn">
                  Member sign in
                </Link>
              </div>

              <div className="mt-10 inline-flex items-center gap-2 text-xs text-slate-400">
                <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>
                  Tap any element below three times to open the member gate.
                  {clicks > 0 && (
                    <span className="ml-2 text-[#D4AF37] font-semibold notranslate">
                      ({clicks}/3)
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Bass-clef ship — shares the 3-tap counter */}
            <div className="lg:col-span-5">
              <button
                type="button"
                onClick={handleTease}
                className="relative aspect-[5/6] w-full max-w-[520px] ml-auto rounded-lg border border-white/5 bg-gradient-to-b from-[#0F1E40]/40 to-[#0A1128]/80 overflow-hidden shadow-[0_25px_80px_-30px_rgba(125,163,217,0.35)] hover:border-[#D4AF37]/40 transition-colors cursor-pointer block text-left"
                data-testid="bass-clef-button"
                title="Tap 3 times to open the member gate"
              >
                <BassClefShip />
                <div className="absolute left-5 bottom-5 right-5 text-[10px] uppercase tracking-[0.3em] text-[#7DA3D9]/80 font-semibold flex items-center justify-between pointer-events-none">
                  <span>Bass Clef · OSRR</span>
                  <span className="text-slate-500">
                    {clicks === 0 ? "Tap 3× to enter" : `${clicks}/3 taps`}
                  </span>
                </div>
                {clicks > 0 && (
                  <div className="absolute top-5 right-5 flex gap-1.5 pointer-events-none">
                    {[1, 2, 3].map((i) => (
                      <span
                        key={i}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          i <= clicks ? "bg-[#D4AF37]" : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Flag grid (tease mode — clicks gate to auth) ============ */}
      <div className="border-t border-white/5 bg-white/[0.012]" data-testid="public-flags">
        <CountryFlagGrid onTease={handleTease} />
      </div>

      {/* ============ Pages in order (chronological list — tease mode) ============ */}
      <section className="border-t border-white/5 section-pad" data-testid="public-pages-list">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-10">
            <div className="w-12 h-12 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-center flex-shrink-0">
              <MapIcon className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <div className="overline" style={{ color: "#D4AF37" }}>Table of contents</div>
              <h2 className="font-display text-3xl lg:text-5xl tracking-tight mt-1 leading-[1.05]">
                Every page on the registry, in order
              </h2>
              <p className="text-slate-400 mt-3 max-w-2xl text-sm leading-relaxed">
                Locked pages open after you sign in and complete the $6.95 paywall. Tap any
                entry — three taps anywhere on this page open the member gate.
              </p>
            </div>
          </div>

          <ol className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-pages-ol">
            {PAGES_IN_ORDER.map((p) => (
              <li key={p.n}>
                <button
                  type="button"
                  onClick={p.locked ? handleTease : undefined}
                  className={`w-full text-left p-4 rounded-md border border-white/10 transition-colors flex items-start gap-3 ${
                    p.locked
                      ? "hover:border-[#D4AF37]/40 hover:bg-white/[0.03]"
                      : "hover:border-[#7DA3D9]/40 hover:bg-white/[0.03]"
                  }`}
                  data-testid={`public-page-${p.n}`}
                >
                  <span className="font-display text-[#D4AF37] text-sm w-8 flex-shrink-0">{p.n}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-200 truncate">{p.label}</div>
                    <div className="text-[10px] text-slate-500 truncate mt-0.5 flex items-center gap-1.5">
                      <span className="font-mono">{p.path}</span>
                      {p.locked && (
                        <>
                          <Lock className="w-3 h-3 text-[#D4AF37]" />
                          <span className="text-[#D4AF37]">members only</span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============ Public legal strip ============ */}
      <section className="relative border-t border-white/5 bg-white/[0.012]">
        <div className="max-w-7xl mx-auto px-6 py-10 text-center">
          <p className="text-xs text-slate-500 leading-relaxed max-w-2xl mx-auto">
            By creating an account you agree to OSRR&apos;s{" "}
            <Link to="/legal/terms" className="underline hover:text-slate-300">Terms</Link>,{" "}
            <Link to="/legal/privacy" className="underline hover:text-slate-300">Privacy Policy</Link>,{" "}
            <Link to="/legal/disclaimer" className="underline hover:text-slate-300">Disclaimer</Link>, and{" "}
            <Link to="/legal/refund" className="underline hover:text-slate-300">Refund Policy</Link>.
          </p>
        </div>
      </section>

      {/* ============ Member gate modal (opens after 3 taps anywhere) ============ */}
      {showGate && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-[#0A1128]/85 backdrop-blur-md"
          data-testid="member-gate"
          onClick={(e) => { if (e.target === e.currentTarget) setShowGate(false); }}
        >
          <div className="relative w-full max-w-md bg-[#0F1E40] border border-[#D4AF37]/30 rounded-lg shadow-[0_30px_100px_-20px_rgba(212,175,55,0.4)] p-8">
            <button
              type="button"
              onClick={() => setShowGate(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-white/5 transition-colors"
              data-testid="member-gate-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="overline mb-3" style={{ color: "#D4AF37" }}>
              Member gate
            </div>
            <h2 className="font-display text-3xl tracking-tight">
              Enter the registry
            </h2>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">
              OSRR is a closed registry. Choose how you&apos;d like to enter — sign in with an
              existing membership, or create a new account and unlock Complete Access for $6.95.
            </p>

            <div className="mt-7 space-y-3">
              <button
                type="button"
                onClick={() => { setShowGate(false); nav("/register"); }}
                className="w-full btn-gold flex items-center justify-center gap-2"
                data-testid="gate-signup"
              >
                <UserPlus className="w-4 h-4" />
                Create an account · $6.95 access
              </button>
              <button
                type="button"
                onClick={() => { setShowGate(false); nav("/login"); }}
                className="w-full btn-ghost-gold flex items-center justify-center gap-2"
                data-testid="gate-signin"
              >
                <LogIn className="w-4 h-4" />
                Sign in to my membership
              </button>
            </div>

            <p className="text-[11px] text-slate-500 text-center mt-6 leading-relaxed">
              One-time $6.95 USD · Lifetime access · Stripe checkout in your local currency.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
